import React, { useMemo } from "react";
import MetricStatCard, { METRIC_STATS_GRID_CLASS } from "./MetricStatCard";
import { useTranslation } from "../../i18n";

const DeletionCaseStatsGrid = ({
  stats,
  loading = false,
  activeStatus = "",
  activeStatusGroup = "",
  activeCreatedWithinDays = null,
  onFilterChange,
}) => {
  const { t } = useTranslation();

  const cards = useMemo(
    () => [
      {
        key: "total",
        label: t("adminStats.total"),
        hint: t("adminStats.totalHint"),
        value: stats?.total,
        filterStatus: "",
        filterStatusGroup: "",
        icon: "folder",
        iconTone: "primary",
        borderClass: "border-primary",
      },
      {
        key: "new",
        label: t("adminStats.new"),
        hint: t("adminStats.newHint"),
        value: stats?.new,
        createdWithinDays: 7,
        icon: "fiber_new",
        iconTone: "secondary",
        borderClass: "border-secondary",
      },
      {
        key: "pending",
        label: t("adminStats.pending"),
        hint: t("adminStats.pendingHint"),
        value: stats?.pending,
        filterStatusGroup: "pending",
        icon: "schedule",
        iconTone: "yellow",
        borderClass: "border-yellow-500",
      },
      {
        key: "in_progress",
        label: t("adminStats.inProgress"),
        hint: t("adminStats.inProgressHint"),
        value: stats?.inProgress,
        filterStatusGroup: "in_progress",
        icon: "autorenew",
        iconTone: "secondary",
        borderClass: "border-secondary",
      },
      {
        key: "completed",
        label: t("adminStats.completed"),
        hint: t("adminStats.completedHint"),
        value: stats?.completed,
        filterStatusGroup: "completed",
        icon: "check_circle",
        iconTone: "green",
        borderClass: "border-green-600",
      },
      {
        key: "rejected",
        label: t("adminStats.rejected"),
        hint: t("adminStats.rejectedHint"),
        value: stats?.rejected,
        filterStatusGroup: "rejected",
        icon: "cancel",
        iconTone: "error",
        borderClass: "border-error",
      },
    ],
    [t, stats]
  );

  const isActive = (card) => {
    if (card.key === "total") {
      return !activeStatus && !activeStatusGroup && !activeCreatedWithinDays;
    }
    if (card.createdWithinDays) {
      return activeCreatedWithinDays === card.createdWithinDays;
    }
    if (card.filterStatusGroup) {
      return activeStatusGroup === card.filterStatusGroup;
    }
    if (card.filterStatus) {
      return activeStatus === card.filterStatus;
    }
    return false;
  };

  if (loading) {
    return (
      <div className={`mb-stack-lg ${METRIC_STATS_GRID_CLASS}`}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="tonal-card h-[140px] animate-pulse rounded-xl border-l-4 border-outline-variant"
          />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="mb-stack-lg">
      <div className={METRIC_STATS_GRID_CLASS}>
        {cards.map((card) => (
          <MetricStatCard
            key={card.key}
            label={card.label}
            hint={card.hint}
            value={card.value ?? 0}
            icon={card.icon}
            iconTone={card.iconTone}
            borderClass={card.borderClass}
            active={isActive(card)}
            onClick={
              onFilterChange
                ? () =>
                    onFilterChange({
                      status: card.filterStatus ?? "",
                      statusGroup: card.filterStatusGroup ?? "",
                      createdWithinDays: card.createdWithinDays ?? null,
                    })
                : undefined
            }
          />
        ))}
      </div>
      {stats.newThisWeek != null && stats.newThisWeek > 0 ? (
        <p className="mt-3 text-body-md text-on-surface-variant">
          {t("adminStats.newCasesWeek", { count: stats.newThisWeek })}
        </p>
      ) : null}
    </div>
  );
};

export default DeletionCaseStatsGrid;
